import { Claim, ClaimStatus, EncounterType, PayerName, DenialReason, AgentAction } from './types';

// Extended claim type for the enhanced tracker
export interface EnhancedClaim extends Claim {
  provider: string;
  confidenceScore: number;
  aiSuggestedCodes: {
    cpt: string[];
    icd10: string[];
    modifiers: string[];
  };
  originalCodes: {
    cpt: string[];
    icd10: string[];
    modifiers: string[];
  };
  codeCorrections: CodeCorrection[];
  payerSimulationChecks: PayerCheck[];
  driftAlerts: DriftAlert[];
  rawNoteExcerpt: string;
  patientDOBLast4: string;
  transmitReady: boolean;
}

export interface CodeCorrection {
  type: 'modifier' | 'cpt' | 'icd10';
  before: string;
  after: string;
  reason: string;
  ncciReference?: string;
}

export interface PayerCheck {
  name: string;
  status: 'passed' | 'warning' | 'failed';
  detail: string;
  policyRef?: string;
}

export interface DriftAlert {
  payer: string;
  changeType: string;
  impact: string;
  suggestedAction: string;
  effectiveDate: string;
}

const PROVIDERS = ['Dr. Larkin', 'Dr. Chen', 'Dr. Rodriguez', 'Dr. Patel', 'Dr. Kim'];
const PAYERS: PayerName[] = ['Medicare', 'BCBS', 'Aetna', 'UnitedHealthcare', 'Cigna', 'Humana'];
const ENCOUNTER_TYPES: EncounterType[] = ['Ortho Surgery', 'Physical Therapy', 'Imaging', 'Office Visit', 'Injection'];

const ORTHO_CPT_CODES: Record<EncounterType, { code: string; description: string; value: number }[]> = {
  'Ortho Surgery': [
    { code: '27447', description: 'Total Knee Arthroplasty', value: 32500 },
    { code: '27130', description: 'Total Hip Arthroplasty', value: 38000 },
    { code: '29881', description: 'Knee Arthroscopy w/ Meniscectomy', value: 8500 },
    { code: '29827', description: 'Shoulder Arthroscopy w/ Rotator Cuff Repair', value: 12000 },
    { code: '27446', description: 'Unicompartmental Knee Arthroplasty', value: 28000 },
    { code: '23412', description: 'Rotator Cuff Repair', value: 9800 },
  ],
  'Physical Therapy': [
    { code: '97110', description: 'Therapeutic Exercise', value: 85 },
    { code: '97140', description: 'Manual Therapy Techniques', value: 95 },
    { code: '97530', description: 'Therapeutic Activities', value: 90 },
    { code: '97542', description: 'Wheelchair Management', value: 75 },
    { code: '97112', description: 'Neuromuscular Re-education', value: 88 },
  ],
  'Imaging': [
    { code: '73721', description: 'MRI Lower Extremity', value: 1200 },
    { code: '73221', description: 'MRI Upper Extremity', value: 1150 },
    { code: '72148', description: 'MRI Lumbar Spine', value: 1400 },
    { code: '73502', description: 'Hip X-Ray', value: 320 },
    { code: '73560', description: 'Knee X-Ray', value: 280 },
  ],
  'Office Visit': [
    { code: '99213', description: 'Office Visit - Established, Low', value: 150 },
    { code: '99214', description: 'Office Visit - Established, Moderate', value: 210 },
    { code: '99215', description: 'Office Visit - Established, High', value: 280 },
    { code: '99203', description: 'Office Visit - New, Low', value: 180 },
    { code: '99204', description: 'Office Visit - New, Moderate', value: 250 },
  ],
  'Injection': [
    { code: '20610', description: 'Joint Injection, Major', value: 450 },
    { code: '64450', description: 'Nerve Block, Other Peripheral', value: 380 },
    { code: '64451', description: 'Nerve Block, Sciatic', value: 420 },
    { code: '64493', description: 'Lumbar Facet Joint Injection', value: 650 },
    { code: '27096', description: 'Sacroiliac Joint Injection', value: 480 },
  ],
};

const ICD10_CODES: Record<EncounterType, { code: string; description: string }[]> = {
  'Ortho Surgery': [
    { code: 'M17.11', description: 'Primary osteoarthritis, right knee' },
    { code: 'M17.12', description: 'Primary osteoarthritis, left knee' },
    { code: 'M16.11', description: 'Primary osteoarthritis, right hip' },
    { code: 'S83.241A', description: 'Complex tear of medial meniscus' },
    { code: 'M75.121', description: 'Complete rotator cuff tear, right shoulder' },
    { code: 'M79.3', description: 'Panniculitis, unspecified' },
  ],
  'Physical Therapy': [
    { code: 'M54.5', description: 'Low back pain' },
    { code: 'M25.561', description: 'Pain in right knee' },
    { code: 'S83.241A', description: 'Complex tear of medial meniscus' },
    { code: 'M75.121', description: 'Complete rotator cuff tear' },
    { code: 'M79.3', description: 'Panniculitis, unspecified' },
  ],
  'Imaging': [
    { code: 'M25.561', description: 'Pain in right knee' },
    { code: 'M54.5', description: 'Low back pain' },
    { code: 'M17.11', description: 'Primary osteoarthritis, right knee' },
    { code: 'M16.11', description: 'Primary osteoarthritis, right hip' },
    { code: 'M79.3', description: 'Panniculitis, unspecified' },
  ],
  'Office Visit': [
    { code: 'M17.11', description: 'Primary osteoarthritis, right knee' },
    { code: 'M25.561', description: 'Pain in right knee' },
    { code: 'M54.5', description: 'Low back pain' },
    { code: 'M79.3', description: 'Panniculitis, unspecified' },
    { code: 'M75.121', description: 'Complete rotator cuff tear' },
  ],
  'Injection': [
    { code: 'M17.11', description: 'Primary osteoarthritis, right knee' },
    { code: 'M54.5', description: 'Low back pain' },
    { code: 'M79.3', description: 'Panniculitis, unspecified' },
    { code: 'M25.561', description: 'Pain in right knee' },
    { code: 'M75.121', description: 'Complete rotator cuff tear' },
  ],
};

const MODIFIERS = [
  { code: '-RT', description: 'Right side' },
  { code: '-LT', description: 'Left side' },
  { code: '-59', description: 'Distinct procedural service' },
  { code: '-25', description: 'Significant, separately identifiable E/M service' },
  { code: '-76', description: 'Repeat procedure by same physician' },
  { code: '-XE', description: 'Separate encounter' },
  { code: '-XS', description: 'Separate structure' },
  { code: '-51', description: 'Multiple procedures' },
];

const RAW_NOTE_EXCERPTS = [
  'Patient presents with progressive right knee pain and stiffness. Conservative management has failed over 6 months. X-rays show severe bone-on-bone arthritis. Recommend total knee arthroplasty.',
  'Post-operative day 14. Incision healing well. No signs of infection. ROM 0-90 degrees. Continue PT protocol.',
  'Patient reports left shoulder pain with overhead activities. MRI confirms full-thickness supraspinatus tear. Discussed surgical options.',
  'Chronic low back pain, worse with prolonged standing. Failed 12 weeks of PT. Facet injection recommended for diagnostic and therapeutic purposes.',
  'Follow-up for right hip replacement. 6 weeks post-op. Excellent progress. Ambulating with single-point cane.',
];

const DENIAL_REASONS: DenialReason[] = ['auth_missing', 'coding_mismatch', 'eligibility', 'bundling', 'timely_filing', 'medical_necessity'];

const INITIALS = ['JD', 'MK', 'RW', 'SL', 'AP', 'TC', 'BH', 'KM', 'LS', 'NP', 'DG', 'EF', 'GH', 'VR', 'WS'];
const DOB_LAST4 = ['0312', '0845', '1127', '0623', '0918', '0204', '1015', '0731', '0429', '1103'];

function randomFromArray<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomBetween(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateClaimId(): string {
  const prefix = 'CLM';
  const timestamp = Date.now().toString(36).toUpperCase().slice(-4);
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
}

function generatePayerChecks(payer: PayerName, encounterType: EncounterType, cptCode: string, icd10Code: string): PayerCheck[] {
  const checks: PayerCheck[] = [
    {
      name: 'Medical Necessity',
      status: 'passed',
      detail: `ICD-10 ${icd10Code} supports medical necessity for CPT ${cptCode}`,
      policyRef: 'LCD L35108'
    },
    {
      name: 'Global Period Overlap',
      status: 'passed',
      detail: 'No global period conflicts detected',
    },
    {
      name: 'Prior Authorization',
      status: encounterType === 'Ortho Surgery' && Math.random() > 0.7 ? 'warning' : 'passed',
      detail: encounterType === 'Ortho Surgery' ? 'Prior auth verified (Auth# PA-2026-12847)' : 'Prior auth not required',
      policyRef: payer === 'UnitedHealthcare' ? 'UHC Ortho Policy 2026.1' : undefined
    },
    {
      name: 'NCCI Bundling Edits',
      status: 'passed',
      detail: 'No bundling conflicts with modifier -59',
      policyRef: 'CMS NCCI v31.1'
    },
  ];

  // Add payer-specific check
  if (payer === 'Aetna') {
    checks.push({
      name: 'Payer Policy Update',
      status: 'passed',
      detail: 'Aetna Jan 2026 modifier guidelines applied',
      policyRef: 'Aetna CPB 0650'
    });
  }

  if (payer === 'Medicare') {
    checks.push({
      name: 'Medicare LCD Compliance',
      status: 'passed',
      detail: 'Meets all LCD requirements for coverage',
      policyRef: 'LCD L35108'
    });
  }

  return checks;
}

function generateCodeCorrections(encounterType: EncounterType): CodeCorrection[] {
  const corrections: CodeCorrection[] = [];
  
  // 30% chance of having a modifier correction
  if (Math.random() > 0.7) {
    corrections.push({
      type: 'modifier',
      before: '-51',
      after: '-59',
      reason: 'Changed from multiple procedure modifier to distinct service modifier per NCCI edits',
      ncciReference: 'NCCI v31.1 Column 2, Code Pair 29881/29880'
    });
  }

  // 20% chance of having a diagnosis sequence correction
  if (Math.random() > 0.8) {
    corrections.push({
      type: 'icd10',
      before: 'M17.9 (Osteoarthritis, unspecified)',
      after: 'M17.11 (Primary osteoarthritis, right knee)',
      reason: 'Laterality required for optimal reimbursement and medical necessity documentation',
    });
  }

  return corrections;
}

function generateDriftAlerts(payer: PayerName): DriftAlert[] {
  const alerts: DriftAlert[] = [];
  
  // 25% chance of having a drift alert
  if (Math.random() > 0.75) {
    const driftScenarios: Record<PayerName, DriftAlert> = {
      'Medicare': {
        payer: 'Medicare',
        changeType: 'Bundling Rules Update',
        impact: 'New NCCI edits for bilateral TKA procedures',
        suggestedAction: 'Add modifier -59 for secondary procedures',
        effectiveDate: 'Jan 15, 2026'
      },
      'Aetna': {
        payer: 'Aetna',
        changeType: 'Prior Auth Requirements',
        impact: 'Now requiring auth for outpatient arthroscopy',
        suggestedAction: 'System auto-requests auth on claim creation',
        effectiveDate: 'Jan 8, 2026'
      },
      'UnitedHealthcare': {
        payer: 'UnitedHealthcare',
        changeType: 'PT Visit Limits',
        impact: 'Reduced PT visit limit from 20 to 15 per episode',
        suggestedAction: 'Request extension after visit 12',
        effectiveDate: 'Jan 1, 2026'
      },
      'BCBS': {
        payer: 'BCBS',
        changeType: 'Imaging Auth',
        impact: 'MRI now requires prior auth for all joints',
        suggestedAction: 'Auto-trigger auth request workflow',
        effectiveDate: 'Jan 10, 2026'
      },
      'Cigna': {
        payer: 'Cigna',
        changeType: 'Modifier Policy',
        impact: 'Stricter enforcement of -XE vs -59 modifiers',
        suggestedAction: 'AI updated to prefer -XE for separate encounters',
        effectiveDate: 'Jan 5, 2026'
      },
      'Humana': {
        payer: 'Humana',
        changeType: 'Reimbursement Rate',
        impact: '4% reduction in allowed amounts for 27447',
        suggestedAction: 'Update fee schedule expectations',
        effectiveDate: 'Jan 1, 2026'
      },
    };
    
    alerts.push(driftScenarios[payer]);
  }

  return alerts;
}

function generateAgentActions(status: ClaimStatus, encounterType: EncounterType, cptCode: string): AgentAction[] {
  const actions: AgentAction[] = [];
  const now = new Date();
  
  const statusOrder: ClaimStatus[] = ['note_signed', 'coding', 'denial_check', 'auto_fix', 'submitted', 'pending', 'paid'];
  const currentIndex = statusOrder.indexOf(status);
  
  if (currentIndex >= 0) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * (currentIndex + 4)),
      agent: 'coding',
      action: 'Clinical note analyzed',
      detail: 'Extracted procedure details, laterality, and diagnosis from clinical documentation using NLP',
      confidence: 0.98,
    });
  }
  
  if (currentIndex >= 1) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * (currentIndex + 3)),
      agent: 'coding',
      action: 'CPT/ICD-10 codes assigned',
      detail: `Assigned ${cptCode} with laterality modifier. Diagnosis sequence optimized for medical necessity.`,
      confidence: 0.96,
    });
  }
  
  if (currentIndex >= 2) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * (currentIndex + 2)),
      agent: 'denial_prevention',
      action: 'Payer simulation complete',
      detail: 'Ran claim through 847 payer-specific rules. All checks passed.',
      confidence: 0.94,
    });
  }
  
  if (currentIndex >= 3) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * (currentIndex + 1)),
      agent: 'auto_fix',
      action: 'Auto-optimization applied',
      detail: 'Modifier -51 replaced with -59 per NCCI edits. Diagnosis laterality added.',
      confidence: 0.92,
    });
  }
  
  if (currentIndex >= 4) {
    actions.push({
      id: `act-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(now.getTime() - 3600000 * currentIndex),
      agent: 'submission',
      action: 'Claim transmitted',
      detail: 'Submitted via EDI 837P to clearinghouse. Acknowledgment received.',
      confidence: 1.0,
    });
  }

  return actions;
}

export function generateEnhancedClaim(overrides?: Partial<EnhancedClaim>): EnhancedClaim {
  const encounterType = randomFromArray(ENCOUNTER_TYPES);
  const payer = randomFromArray(PAYERS);
  const provider = randomFromArray(PROVIDERS);
  const cptData = randomFromArray(ORTHO_CPT_CODES[encounterType]);
  const icd10Data = randomFromArray(ICD10_CODES[encounterType]);
  const modifier = randomFromArray(MODIFIERS);
  
  const status = randomFromArray([
    'coding', 'denial_check', 'auto_fix', 'submitted', 'pending', 'paid'
  ] as ClaimStatus[]);
  
  const confidenceScore = status === 'paid' 
    ? randomBetween(96, 100) 
    : status === 'pending' || status === 'submitted'
    ? randomBetween(92, 99)
    : randomBetween(85, 98);

  const expectedAmount = cptData.value * (Math.random() > 0.7 ? 2 : 1); // Sometimes multiple units
  const allowedAmount = Math.floor(expectedAmount * (0.65 + Math.random() * 0.25));
  const patientResponsibility = Math.floor(allowedAmount * (0.1 + Math.random() * 0.15));
  
  const denialRisk = 100 - confidenceScore + randomBetween(-5, 5);
  const codeCorrections = generateCodeCorrections(encounterType);

  const originalModifiers = codeCorrections.some(c => c.type === 'modifier') 
    ? ['-51'] 
    : [modifier.code];

  const aiModifiers = codeCorrections.some(c => c.type === 'modifier')
    ? [modifier.code]
    : [modifier.code];

  return {
    id: generateClaimId(),
    patientInitials: randomFromArray(INITIALS),
    patientDOBLast4: randomFromArray(DOB_LAST4),
    encounterType,
    encounterDate: new Date(Date.now() - randomBetween(1, 14) * 86400000),
    payer,
    provider,
    cptCodes: [cptData.code],
    icd10Codes: [icd10Data.code],
    modifiers: aiModifiers,
    expectedAmount,
    allowedAmount,
    patientResponsibility,
    denialRisk: Math.max(0, Math.min(100, denialRisk)),
    denialReasons: denialRisk > 20 ? [randomFromArray(DENIAL_REASONS)] : [],
    status,
    agentActions: generateAgentActions(status, encounterType, cptData.code),
    submittedAt: ['submitted', 'pending', 'paid', 'denied'].includes(status) 
      ? new Date(Date.now() - randomBetween(1, 7) * 86400000) 
      : undefined,
    paidAt: status === 'paid' ? new Date(Date.now() - randomBetween(0, 3) * 86400000) : undefined,
    autoFixesApplied: codeCorrections.length,
    facility: 'Clinical Partner Practice',
    confidenceScore,
    aiSuggestedCodes: {
      cpt: [cptData.code],
      icd10: [icd10Data.code],
      modifiers: aiModifiers,
    },
    originalCodes: {
      cpt: [cptData.code],
      icd10: codeCorrections.some(c => c.type === 'icd10') ? ['M17.9'] : [icd10Data.code],
      modifiers: originalModifiers,
    },
    codeCorrections,
    payerSimulationChecks: generatePayerChecks(payer, encounterType, cptData.code, icd10Data.code),
    driftAlerts: generateDriftAlerts(payer),
    rawNoteExcerpt: randomFromArray(RAW_NOTE_EXCERPTS),
    transmitReady: confidenceScore >= 95 && status !== 'paid',
    ...overrides,
  };
}

export function generateEnhancedClaims(count: number): EnhancedClaim[] {
  const claims: EnhancedClaim[] = [];
  
  // Ensure some claims are from Dr. Larkin and high-value
  for (let i = 0; i < count; i++) {
    const isLarkinClaim = i < Math.floor(count * 0.4); // 40% Dr. Larkin
    const isHighValue = i < Math.floor(count * 0.25); // 25% high-value surgeries
    
    const overrides: Partial<EnhancedClaim> = {};
    
    if (isLarkinClaim) {
      overrides.provider = 'Dr. Larkin';
    }
    
    if (isHighValue) {
      overrides.encounterType = 'Ortho Surgery';
    }
    
    claims.push(generateEnhancedClaim(overrides));
  }
  
  // Shuffle the claims
  return claims.sort(() => Math.random() - 0.5);
}

export type QuickFilter = 'all' | 'high_risk' | 'needs_validation' | 'high_value' | 'ready_to_transmit';

export function applyQuickFilter(claims: EnhancedClaim[], filter: QuickFilter): EnhancedClaim[] {
  switch (filter) {
    case 'high_risk':
      return claims.filter(c => c.denialRisk > 20);
    case 'needs_validation':
      return claims.filter(c => c.confidenceScore < 95);
    case 'high_value':
      return claims.filter(c => c.expectedAmount > 5000);
    case 'ready_to_transmit':
      return claims.filter(c => c.transmitReady);
    default:
      return claims;
  }
}
